import { useState, useEffect, useCallback } from "react";

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

/**
 * Approximates page zoom vs a nominal 100% and returns a multiplier for typography.
 * Uses VisualViewport when available; no devicePixelRatio (Retina-safe).
 * Not exact on every OS/browser; rem-based sizing still respects native browser zoom.
 */
function estimateZoomPercent() {
  if (typeof window === "undefined") return 100;

  const vv = window.visualViewport;

  if (vv && typeof vv.scale === "number" && vv.scale > 0) {
    if (Math.abs(vv.scale - 1) >= 0.02) {
      return clamp(Math.round(100 * vv.scale), 50, 200);
    }
    if (vv.width > 0 && window.innerWidth > 0) {
      const q = window.innerWidth / vv.width;
      if (Number.isFinite(q) && q > 1.008) {
        return clamp(Math.round(100 * q), 75, 140);
      }
      if (Number.isFinite(q) && q < 0.992) {
        return clamp(Math.round(100 * q), 70, 125);
      }
    }
  }

  const docEl = document.documentElement;
  if (docEl && window.innerWidth > 0) {
    const r = docEl.clientWidth / window.innerWidth;
    if (Number.isFinite(r) && r > 0.88 && r < 1.12 && Math.abs(r - 1) >= 0.02) {
      return clamp(Math.round(100 * r), 80, 120);
    }
  }

  return 100;
}

/** 100% → 1; each ±1 zoom point adjusts ~0.6% size (bounded). */
function zoomPercentToFontScale(zoomPct) {
  const z = clamp(zoomPct, 70, 140);
  return clamp(1 + (z - 100) * 0.006, 0.85, 1.15);
}

export function useBrowserZoomFontScale() {
  const [scale, setScale] = useState(() =>
    zoomPercentToFontScale(estimateZoomPercent())
  );

  const update = useCallback(() => {
    setScale(zoomPercentToFontScale(estimateZoomPercent()));
  }, []);

  useEffect(() => {
    let raf;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("resize", schedule);
    const vv = window.visualViewport;
    vv?.addEventListener?.("resize", schedule);
    vv?.addEventListener?.("scroll", schedule);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", schedule);
      vv?.removeEventListener?.("resize", schedule);
      vv?.removeEventListener?.("scroll", schedule);
    };
  }, [update]);

  return scale;
}
